import React, { useEffect, useRef, useState } from 'react';

import { OnboardingTitle } from 'registration/containers/Onboarding';

import { UNSELECTED } from 'shared/components/ihcl/button_select';
import { DatePicker } from 'shared/components/ihcl/datepicker';
import { styled, withStyle } from 'shared/components/ihcl/styled';
import { ButtonSelect } from 'shared/components/ihcl/v2/button-select';
import {
  jobSearchOptions,
  recentGradWithStudentOptions,
  startDateOptions,
  startDateOptionsNotLooking,
} from 'shared/constants/selectOptions';
import { trackClick } from 'shared/helpers/tracking';

import { useOnboardingStepContext } from '../providers/OnboardingStepProvider';
import { useOnboardingVariantContext } from '../providers/OnboardingVariantProvider';
import StickySubmitFooter from './StickySubmitFooter';
import { ErrorMessage, Prompt } from './styledComponents';

const minDate = new Date();
const maxDate = new Date(
  minDate.getFullYear() + 2,
  minDate.getMonth(),
  minDate.getDate()
);

const DatePickerWrapper = styled('div', () => ({
  marginTop: '1em',
  maxWidth: '250px',
}));

const IsolatedSteps = styled('div', () => ({
  minHeight: '150vh',
}));

const FirstIsolatedStep = styled('div', () => ({
  width: '100%',
}));

const IsolatedStep = withStyle<any, { $collapseTopPadding?: boolean }>(
  FirstIsolatedStep,
  ({ $collapseTopPadding = false }) => ({
    paddingTop: $collapseTopPadding ? 0 : '40px',
  })
);

const OnboardingTimeline = () => {
  /* eslint-disable @typescript-eslint/naming-convention */
  const {
    maybeGoToNext,
    isUpdatingProfile,
    localStepTalentProfile,
    setLocalStepTalentProfile,
  } = useOnboardingStepContext();
  const {
    education_level,
    job_search_status,
    license_issue_date,
    start_date,
    start_date_option,
    recent_grad,
  } = localStepTalentProfile;
  const { intent } = useOnboardingVariantContext();
  const startDateRef = useRef(null);
  const recentGradRef = useRef(null);
  const [errors, setErrors] = useState(null);
  const currentlyLooking = job_search_status !== 'not_looking';
  const recentGradSet = [true, false].includes(recent_grad);

  useEffect(() => {
    if (
      job_search_status &&
      job_search_status !== UNSELECTED &&
      job_search_status !== 'not_looking' &&
      !start_date_option
    ) {
      startDateRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [
    education_level,
    job_search_status,
    start_date_option,
    start_date,
    recent_grad,
    currentlyLooking,
    recentGradSet,
  ]);
  const isContinueDisabled =
    !job_search_status ||
    job_search_status === UNSELECTED ||
    !start_date_option ||
    (start_date_option === 'specific_date' && !start_date) ||
    // (start_date_option === 'not_sure' && !travelNurseSet) ||
    !recentGradSet;
  const submit = () => {
    if (isContinueDisabled) {
      setErrors('Please answer all questions before continuing.');
      return;
    }

    trackClick('talent.onboarding.submit', {
      Intent: intent,
    });
    maybeGoToNext();
  };
  const startDateOptionsList = currentlyLooking
    ? startDateOptions
    : startDateOptionsNotLooking;

  const recentGradOptions = recentGradWithStudentOptions;
  return (
    <>
      <div>
        <Prompt>
          <OnboardingTitle>
            Great! What&apos;s your ideal timeline?
          </OnboardingTitle>
          {errors && <ErrorMessage>{errors}</ErrorMessage>}
        </Prompt>
        <IsolatedSteps>
          {currentlyLooking && (
            <FirstIsolatedStep>
              <p>How far are you in your search?</p>
              <ButtonSelect
                className="flex-col items-center"
                hideUnselected
                options={jobSearchOptions}
                selectionMode="single"
                values={job_search_status ? [job_search_status] : []}
                onValuesChange={([optionValue]) => {
                  if (optionValue === 'not_looking') {
                    trackClick('Not looking selected');
                  }
                  setLocalStepTalentProfile({
                    ...localStepTalentProfile,
                    job_search_status: optionValue || UNSELECTED,
                  });
                }}
              />
            </FirstIsolatedStep>
          )}
          {((job_search_status && job_search_status !== UNSELECTED) ||
            start_date_option) && (
            <IsolatedStep
              $collapseTopPadding={!currentlyLooking}
              ref={startDateRef}
            >
              <p>{currentlyLooking && 'When are you able to start?'}</p>
              <ButtonSelect
                className="flex-col items-center"
                hideUnselected
                options={startDateOptionsList.toArray()}
                selectionMode="single"
                values={start_date_option ? [start_date_option] : []}
                onValuesChange={([optionValue]) => {
                  setLocalStepTalentProfile({
                    ...localStepTalentProfile,
                    start_date_option: optionValue ?? null,
                  });
                }}
              />
              {start_date_option === 'specific_date' && (
                <DatePickerWrapper>
                  <DatePicker
                    autoFocusCalendar
                    minDate={minDate}
                    maxDate={maxDate}
                    value={start_date}
                    onChange={(obj) => {
                      setLocalStepTalentProfile({
                        ...localStepTalentProfile,
                        start_date: obj.date,
                      });
                    }}
                  />
                </DatePickerWrapper>
              )}
            </IsolatedStep>
          )}
          {((start_date_option && start_date_option !== 'specific_date') ||
            (start_date_option === 'specific_date' && start_date)) && (
            <IsolatedStep ref={recentGradRef}>
              <p>How long have you been working as an RN?</p>
              <ButtonSelect
                className="flex-col items-center"
                hideUnselected
                options={recentGradOptions}
                selectionMode="single"
                values={recentGradOptions
                  .filter((option) => {
                    if (education_level) {
                      if (
                        option.value === education_level &&
                        license_issue_date === undefined
                      ) {
                        return true;
                      }
                      if (
                        option.value === 'recent_grad_no_license' &&
                        license_issue_date === null
                      ) {
                        return true;
                      }
                    }
                    return option.value === recent_grad;
                  })
                  .map((option) => option.value)}
                onValuesChange={([optionValue]) => {
                  if (optionValue === undefined) {
                    const newLocalStepTalentProfile = {
                      ...localStepTalentProfile,
                    };
                    delete newLocalStepTalentProfile.education_level;
                    delete newLocalStepTalentProfile.recent_grad;
                    setLocalStepTalentProfile(newLocalStepTalentProfile);
                    return;
                  }
                  const localStepTalentProfileUpdate: any = {
                    recent_grad: Boolean(optionValue),
                  };
                  if (optionValue === 'student') {
                    localStepTalentProfileUpdate.education_level = 'student';
                    localStepTalentProfileUpdate.license_issue_date = undefined;
                  }
                  if (optionValue === 'recent_grad_no_license') {
                    localStepTalentProfileUpdate.education_level = 'student';
                    localStepTalentProfileUpdate.license_issue_date = null;
                  }
                  setLocalStepTalentProfile({
                    ...localStepTalentProfile,
                    ...localStepTalentProfileUpdate,
                  });
                }}
              />
            </IsolatedStep>
          )}
        </IsolatedSteps>
      </div>

      <StickySubmitFooter
        onClick={submit}
        disabled={isUpdatingProfile || isContinueDisabled}
        isLoading={isUpdatingProfile}
      />
    </>
  );
};

export default OnboardingTimeline;
